import React from 'react';

/**
 * RValueCoverage — Iter18 R2 §1.5.1 slice E.4
 * ---------------------------------------------------------------------------
 * Manual R-value coverage grid (23 R-values, from LEGACY-QI-3 reference wire):
 *
 *   R-4, R-6, R-7, R-8, R-10, R-11, R-12, R-13, R-19, R-20, R-21, R-22, R-23,
 *   R-24, R-25, R-26, R-38, R-38HP, R-44, R-45, R-49, R-50, R-60
 *
 * Each cell is a numeric input; blank = product doesn't achieve that R-value.
 * Values are entered manually from manufacturer specs (no auto-calc).
 *
 * Layout: 3-column labeled input grid (matches legacy column-flow chrome).
 *
 * Consumers:
 *   • Batts (single grid — passed directly)
 *   • Attic/wall blow categories (two instances wrapped by AtticWallToggle)
 */

// IDI-428 (09-29 call): R30 was missing ("the most common probably for
// blown-in"); OC PROPINK BLOWING WOOL 33 LB carries R13, R30 and R50. The
// ladder now covers every value IDI's charts use, in ascending order, and
// the admin can add any other R-value the chart needs (see "Add R-value").
export const R_VALUES = [
  'R-4', 'R-6', 'R-7', 'R-8', 'R-10', 'R-11', 'R-12', 'R-13', 'R-15',
  'R-19', 'R-20', 'R-21', 'R-22', 'R-23', 'R-24', 'R-25', 'R-26', 'R-30',
  'R-38', 'R-38HP', 'R-44', 'R-45', 'R-49', 'R-50', 'R-60',
];

// Numeric sort key for an R label: "R-38HP" -> 38.5 (just after R-38).
export function rSortKey(label) {
  const m = String(label).toUpperCase().match(/R-?(\d+(?:\.\d+)?)(.*)$/);
  if (!m) return Number.POSITIVE_INFINITY;
  return Number(m[1]) + (m[2] ? 0.5 : 0);
}

// The chart's rows: the standard ladder plus any R-value already present on
// this product (a migrated or admin-added one), deduped, ascending.
export function chartRValues(values) {
  const set = new Set(R_VALUES);
  Object.keys(values || {}).forEach((k) => set.add(k));
  return Array.from(set).sort((a, b) => rSortKey(a) - rSortKey(b));
}

export default function RValueCoverage({ values, onChange }) {
  const [extra, setExtra] = React.useState('');
  const rows = chartRValues(values);
  const addRValue = () => {
    const n = String(extra).trim().replace(/^r-?/i, '');
    if (!/^\d+(\.\d+)?[A-Za-z]*$/.test(n)) return;
    const label = `R-${n.toUpperCase()}`;
    if (!(label in (values || {}))) onChange({ ...(values || {}), [label]: null });
    setExtra('');
  };
  const setValue = (label, raw) => {
    const next = { ...(values || {}) };
    if (raw === '' || raw === null || Number.isNaN(Number(raw))) {
      next[label] = null;
    } else {
      next[label] = Number(raw);
    }
    onChange(next);
  };

  // IDI-428: values stack DOWN columns, ascending (R4, R6, R7, R8 down the
  // first column, continuing in the next), so a gap is visible at a glance.
  // grid-auto-flow: column with a fixed row count does exactly that.
  const perColumn = Math.ceil(rows.length / 2);
  return (
    <div>
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5"
      data-testid="rvalue-coverage-grid"
      style={{ gridAutoFlow: 'column', gridTemplateRows: `repeat(${perColumn}, auto)` }}
    >
      {rows.map((label) => {
        const v = values?.[label];
        return (
          <label
            key={label}
            className="flex items-center gap-2 text-xs"
            style={{ color: 'var(--color-text-default)' }}
          >
            <span
              className="inline-block text-right font-medium"
              style={{ minWidth: '4.5rem', color: 'var(--color-text-muted)' }}
            >
              {label}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={v ?? ''}
              onChange={(e) => setValue(label, e.target.value)}
              className="flex-1 px-2 py-1 rounded border text-xs"
              style={{ borderColor: 'var(--color-border)' }}
              placeholder="—"
              aria-label={`Coverage (sq ft) at ${label}`}
            />
          </label>
        );
            })}
    </div>
      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRValue(); } }}
          placeholder="e.g. 33"
          aria-label="Add an R-value to this chart"
          className="w-24 px-2 py-1 rounded border text-xs"
          style={{ borderColor: 'var(--color-border)' }}
        />
        <button type="button" onClick={addRValue} className="text-xs underline" style={{ color: 'var(--color-action-primary)' }}>
          Add R-value
        </button>
      </div>
    </div>
  );
}
